import { useEffect, useState } from "react";
import { API, authFetch, useAuth } from "@/App";
import { toast } from "sonner";
import { Gift, Award, TrendingUp, Package, Sparkles } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const DealerLoyaltyCard = ({ partyId, firmName }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);

  useEffect(() => {
    if (!partyId) return;
    (async () => {
      try {
        const res = await authFetch(`${API}/loyalty/dealer/${partyId}`);
        const d = await res.json();
        if (d.success) setData(d.data);
      } finally { setLoading(false); }
    })();
  }, [partyId]);

  const redeem = async (giftId, points) => {
    if (!window.confirm(`Redeem this gift for ${points} points?`)) return;
    setRedeeming(giftId);
    try {
      const res = await authFetch(`${API}/loyalty/redeem`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ party_id: partyId, gift_id: giftId }),
      });
      const d = await res.json();
      if (d.success) {
        toast.success(`Redemption created: ${d.data.gift_name}`);
        // Refresh
        const r = await authFetch(`${API}/loyalty/dealer/${partyId}`);
        const dd = await r.json();
        if (dd.success) setData(dd.data);
      } else toast.error(d.detail || "Failed to redeem");
    } catch { toast.error("Network error"); }
    finally { setRedeeming(null); }
  };

  if (loading) return <div className="py-8 text-center"><div className="w-6 h-6 spinner mx-auto" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="dealer-loyalty-card">
      {/* Hero stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4 bg-gradient-to-br from-[#8B1A1A] to-[#6B1515] text-white">
          <div className="flex items-center gap-2 mb-1">
            <Award className="w-4 h-4" />
            <p className="text-xs uppercase opacity-80">Available</p>
          </div>
          <p className="text-3xl font-bold font-mono" data-testid="loyalty-available">{data.points_available}</p>
          <p className="text-xs opacity-70 mt-1">points to redeem</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
            <p className="text-xs uppercase text-slate-500">Earned</p>
          </div>
          <p className="text-2xl font-bold font-mono text-emerald-600" data-testid="loyalty-earned">{data.points_earned}</p>
          <p className="text-xs text-slate-500 mt-1">lifetime points</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <Package className="w-4 h-4 text-blue-600" />
            <p className="text-xs uppercase text-slate-500">Bags</p>
          </div>
          <p className="text-2xl font-bold font-mono text-blue-600" data-testid="loyalty-bags">{data.total_bags_purchased}</p>
          <p className="text-xs text-slate-500 mt-1">5 pts per 2 bags</p>
        </Card>
      </div>

      {/* Gift catalog */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Gift className="w-4 h-4 text-[#8B1A1A]" />
          <h4 className="font-semibold text-slate-800">Gift Catalog</h4>
          <Badge variant="outline" className="text-[10px]">{data.gifts?.length || 0} items</Badge>
        </div>
        {(!data.gifts || data.gifts.length === 0) ? (
          <p className="text-sm text-slate-400 text-center py-6">No gifts available</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.gifts.map(gift => {
              const canAfford = data.points_available >= gift.points_required;
              return (
                <Card key={gift.gift_id} className="p-3" data-testid={`gift-${gift.gift_id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-slate-800">{gift.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {gift.category && <Badge variant="outline" className="text-[10px]">{gift.category}</Badge>}
                        <span className="text-xs font-mono text-[#8B1A1A] font-bold">{gift.points_required} pts</span>
                      </div>
                    </div>
                    <Button size="sm" disabled={!canAfford || redeeming === gift.gift_id}
                      onClick={() => redeem(gift.gift_id, gift.points_required)}
                      className={canAfford ? "btn-primary h-7 text-xs" : "h-7 text-xs"} variant={canAfford ? "default" : "outline"}
                      data-testid={`redeem-${gift.gift_id}`}>
                      {redeeming === gift.gift_id ? "..." : canAfford ? <><Sparkles className="w-3 h-3 mr-1" /> Redeem</> : "Not enough"}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DealerLoyaltyCard;
